import { Tooltip } from '@crazyegginc/hatch';
import { ReactComponent as CopyIcon } from '@crazyegginc/hatch/dist/images/icon-pages-outline.svg';
import { ReactComponent as MailIcon } from '@crazyegginc/hatch/dist/images/icon-mail.svg';
import { useEffect, useRef, useState } from 'react';

export function CodeSnippet({ code, children, showMailButton = false, onMailButtonClick }) {
  return (
    <div className="flex p-5 gap-1 items-start justify-center rounded bg-zumthor-500">
      <pre className="min-w-0 flex-1 whitespace-pre-wrap break-words text-lynch-500 text-sm sm:text-base font-normal leading-[30px]">
        {code}
      </pre>
      <div className="flex flex-col sm:flex-row gap-1 flex-shrink-0">
        {children}

        {showMailButton ? (
          <SnippetButton onClick={onMailButtonClick} tooltipContent="Email script to my developer">
            <MailIcon className="w-4 h-4 fill-current" />
          </SnippetButton>
        ) : null}

        <CopyButton code={code} />
      </div>
    </div>
  );
}

export function SnippetButton({ children, onClick, tooltipContent }) {
  const button = (
    <button
      onClick={onClick}
      aria-label={tooltipContent}
      className="bg-none border-none text-dodger-blue-500 w-[36px] h-[35px] flex items-center justify-center"
    >
      {children}
    </button>
  );

  if (!tooltipContent) {
    return button;
  }

  return <Tooltip tooltipContent={tooltipContent}>{button}</Tooltip>;
}

export function CopyButton({ code }) {
  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false);
  const hideTooltipTimeoutRef = useRef(null);
  const tooltipKey = showCopiedTooltip ? 'copied' : 'copy';
  const tooltipContent = showCopiedTooltip ? 'Copied' : 'Copy code block';

  const clearHideTooltipTimeout = () => {
    if (hideTooltipTimeoutRef.current) {
      clearTimeout(hideTooltipTimeoutRef.current);
      hideTooltipTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      clearHideTooltipTimeout();
    };
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      // noop
    }

    setShowCopiedTooltip(true);
    clearHideTooltipTimeout();

    hideTooltipTimeoutRef.current = setTimeout(() => {
      setShowCopiedTooltip(false);
      clearHideTooltipTimeout();
    }, 2000);
  };

  return (
    <Tooltip
      key={tooltipKey}
      tooltipContent={tooltipContent}
      show={showCopiedTooltip ? true : undefined}
      hover={showCopiedTooltip ? false : true}
    >
      <button
        onClick={handleCopy}
        className="bg-none border-none text-dodger-blue-500 w-[36px] h-[35px] flex items-center justify-center"
        aria-label="Copy code"
      >
        <CopyIcon className="w-4 h-4 fill-current" aria-label="Copy code" />
      </button>
    </Tooltip>
  );
}
